import { API_URL, API_BASE_URL } from '../config/api';

/**
 * Compute normalized backend origin/base URL with any trailing `/api` (or `/api/v1`) stripped,
 * so relative media paths like `/uploads/video-shorts/...` resolve reliably across environments.
 */
export function getBackendMediaBaseUrl() {
    const candidates = [
        process.env.REACT_APP_MEDIA_BASE_URL,
        process.env.REACT_APP_BACKEND_URL,
        process.env.REACT_APP_BASE_URL,
        API_URL && !API_URL.startsWith('/') ? API_URL : '',
        API_BASE_URL,
    ];

    const chosen = candidates
        .map((val) => (typeof val === 'string' ? val.trim() : ''))
        .find((val) => Boolean(val));

    if (!chosen) return '';

    return chosen
        .replace(/\/+$/, '')
        .replace(/\/api(?:\/v\d+)?$/i, '');
}

/**
 * Resolve product, blog, thumbnail, or uploaded video media path to a full URL.
 * Supports:
 * - Full URLs (`http://`, `https://`, `//`, `blob:`, `data:`)
 * - Frontend public/bundled assets (`/assets/...`, `/static/...`)
 * - Relative backend upload paths (`/uploads/video-shorts/...`, `uploads/...`)
 * - Object wrappers (`{ url, path, secure_url, src }`)
 *
 * @param {string|object|null|undefined} mediaPath
 * @returns {string|null}
 */
export function resolveMediaUrl(mediaPath) {
    if (!mediaPath) return null;

    let raw = mediaPath;
    if (typeof raw === 'object') {
        raw = raw.url || raw.secure_url || raw.path || raw.src || '';
    }
    if (typeof raw !== 'string') return null;

    const trimmed = raw.trim();
    if (!trimmed) return null;

    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('blob:')
    ) {
        return trimmed;
    }

    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }

    // Normalize any Windows-style backslashes from backend file paths
    const normalized = trimmed.replace(/\\/g, '/');
    const cleanPath = normalized.startsWith('/') ? normalized : `/${normalized}`;

    // Frontend public or bundled assets (e.g. /assets/...)
    if (cleanPath.startsWith('/assets/') || cleanPath.startsWith('/static/')) {
        const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
        return `${publicUrl}${cleanPath}`;
    }

    const base = getBackendMediaBaseUrl();
    return base ? `${base}${cleanPath}` : cleanPath;
}

export function getImageUrl(imagePath) {
    return resolveMediaUrl(imagePath);
}

export default getImageUrl;
